const DEVICE_ID_STORAGE_KEY = 'skillarea.device-id.v1';
const DEVICE_ID_REFRESH_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;
const DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const DEVICE_ID_MAX_LENGTH = 128;

type TBrowserStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

interface IStoredDeviceIdRecord {
  visitorId: string;
  storedAt: number;
}

let cachedDeviceId: string | null = null;
let inFlightPromise: Promise<string | null> | null = null;

function safeGetLocalStorage(): TBrowserStorage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return (window.localStorage as TBrowserStorage | undefined) ?? null;
  } catch {
    return null;
  }
}

function readStoredDeviceId(): IStoredDeviceIdRecord | null {
  const storage = safeGetLocalStorage();
  if (storage === null) {
    return null;
  }

  try {
    const raw = storage.getItem(DEVICE_ID_STORAGE_KEY);
    if (raw === null) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<IStoredDeviceIdRecord>;
    if (
      typeof parsed.visitorId !== 'string' ||
      parsed.visitorId.length === 0 ||
      parsed.visitorId.length > DEVICE_ID_MAX_LENGTH ||
      !DEVICE_ID_PATTERN.test(parsed.visitorId) ||
      typeof parsed.storedAt !== 'number'
    ) {
      return null;
    }

    return { visitorId: parsed.visitorId, storedAt: parsed.storedAt };
  } catch {
    return null;
  }
}

function writeStoredDeviceId(visitorId: string): void {
  const storage = safeGetLocalStorage();
  if (storage === null) {
    return;
  }

  try {
    const record: IStoredDeviceIdRecord = {
      visitorId,
      storedAt: Date.now(),
    };
    storage.setItem(DEVICE_ID_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // storage quota or private mode — silently skip
  }
}

function isRecordStale(record: IStoredDeviceIdRecord): boolean {
  return Date.now() - record.storedAt > DEVICE_ID_REFRESH_INTERVAL_MS;
}

async function computeFreshDeviceId(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const FingerprintJS = await import('@fingerprintjs/fingerprintjs');
    const agent = await FingerprintJS.load();
    const result = await agent.get();
    const visitorId = result.visitorId;

    if (
      typeof visitorId !== 'string' ||
      visitorId.length === 0 ||
      visitorId.length > DEVICE_ID_MAX_LENGTH ||
      !DEVICE_ID_PATTERN.test(visitorId)
    ) {
      return null;
    }

    return visitorId;
  } catch {
    return null;
  }
}

export function getCachedDeviceId(): string | null {
  if (cachedDeviceId !== null) {
    return cachedDeviceId;
  }

  const stored = readStoredDeviceId();
  if (stored !== null) {
    cachedDeviceId = stored.visitorId;
    return cachedDeviceId;
  }

  return null;
}

export async function ensureDeviceId(): Promise<string | null> {
  if (cachedDeviceId !== null) {
    const stored = readStoredDeviceId();
    if (stored !== null && !isRecordStale(stored)) {
      return cachedDeviceId;
    }
  } else {
    const stored = readStoredDeviceId();
    if (stored !== null && !isRecordStale(stored)) {
      cachedDeviceId = stored.visitorId;
      return cachedDeviceId;
    }
  }

  if (inFlightPromise !== null) {
    return await inFlightPromise;
  }

  inFlightPromise = (async () => {
    const fresh = await computeFreshDeviceId();
    if (fresh !== null) {
      cachedDeviceId = fresh;
      writeStoredDeviceId(fresh);
      return fresh;
    }

    return cachedDeviceId;
  })();

  try {
    return await inFlightPromise;
  } finally {
    inFlightPromise = null;
  }
}
